import { useState } from "react";
import { Check, X, ArrowRight, RotateCcw } from "lucide-react";

/**
 * Lesson quiz with immediate feedback + explanation.
 * questions include correttaIndex and spiegazione (learning mode).
 * onFinished(correct, total) is called once when the last question is answered.
 */
export default function LessonQuiz({ questions, onFinished }) {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);

  if (!questions || questions.length === 0) {
    return (
      <p className="text-sm text-[color:var(--text-2)]" data-testid="lesson-quiz-empty">
        Nessuna domanda disponibile per questa lezione.
      </p>
    );
  }

  const total = questions.length;
  const q = questions[idx];

  const submit = (optionIdx) => {
    if (answered) return;
    setSelected(optionIdx);
    setAnswered(true);
    if (optionIdx === q.correttaIndex) setCorrectCount((c) => c + 1);
  };

  const next = () => {
    if (idx + 1 < total) {
      setIdx(idx + 1);
      setSelected(null);
      setAnswered(false);
    } else {
      const finalCorrect = correctCount;
      setFinished(true);
      if (onFinished) onFinished(finalCorrect, total);
    }
  };

  const restart = () => {
    setIdx(0);
    setSelected(null);
    setAnswered(false);
    setCorrectCount(0);
    setFinished(false);
  };

  if (finished) {
    const passed = correctCount / total >= 0.6;
    return (
      <div className="text-center py-6 fade-up" data-testid="lesson-quiz-summary">
        <p className="font-mono text-4xl font-semibold tracking-tight">
          {correctCount}/{total}
        </p>
        <p className={`mt-2 font-extrabold uppercase tracking-wide text-sm ${passed ? "" : "text-[color:var(--danger)]"}`}>
          {passed ? `${correctCount} su ${total} — superato.` : "Ripassa e riprova."}
        </p>
        <button
          type="button"
          onClick={restart}
          data-testid="lesson-quiz-retry-button"
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-[color:var(--hairline)] bg-white px-5 py-2.5 font-semibold text-sm hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
        >
          <RotateCcw className="w-4 h-4" /> Riprova il quiz
        </button>
      </div>
    );
  }

  return (
    <div data-testid="lesson-quiz">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-xs tracking-widest uppercase text-[color:var(--text-2)]">
          Domanda {idx + 1}/{total}
        </span>
        <span className="font-mono text-xs text-[color:var(--text-2)]">{correctCount} corrette</span>
      </div>

      <p className="font-bold text-[15px] md:text-base leading-6 mb-4" data-testid="lesson-quiz-question-text">
        {q.testo}
      </p>

      <div className="space-y-2.5">
        {q.opzioni.map((opt, i) => {
          const isCorrect = answered && i === q.correttaIndex;
          const isWrongSel = answered && selected === i && i !== q.correttaIndex;
          return (
            <button
              key={i}
              type="button"
              disabled={answered}
              onClick={() => submit(i)}
              data-testid="lesson-quiz-answer-option"
              className={`w-full text-left rounded-2xl border px-4 py-3.5 min-h-[44px] text-sm leading-5 transition-colors duration-200 flex items-start justify-between gap-3 ${
                isCorrect
                  ? "border-transparent bg-[color:var(--volt)] text-[color:var(--ink)] font-bold"
                  : isWrongSel
                  ? "border-[color:var(--danger)] bg-white text-[color:var(--danger)] font-semibold"
                  : answered
                  ? "border-[color:var(--hairline)] bg-white text-[color:var(--text-2)]"
                  : "border-[color:var(--hairline)] bg-white hover:bg-[color:var(--bg-subtle)] cursor-pointer"
              }`}
            >
              <span>{opt}</span>
              {isCorrect && <Check className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={3} />}
              {isWrongSel && <X className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={3} />}
            </button>
          );
        })}
      </div>

      {answered && (
        <div className="mt-4 rounded-2xl bg-[color:var(--bg-subtle)] border border-[color:var(--hairline)] p-4 fade-up" data-testid="lesson-quiz-explanation">
          <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--text-2)] mb-1">Spiegazione</p>
          <p className="text-sm leading-6">{q.spiegazione}</p>
        </div>
      )}

      {answered && (
        <button
          type="button"
          onClick={next}
          data-testid="lesson-quiz-next-button"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-3.5 hover:bg-black/85 transition-colors duration-200"
        >
          {idx + 1 < total ? "Prossima domanda" : "Vedi risultato"} <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
